/**
 * Terms of Service and Privacy Policy.
 *
 * Real documents now (they replaced the "coming soon" placeholders): the app's
 * feature set is settled enough to describe honestly, and they need to be in
 * place before the site is shown to anyone — a reviewer, a first user — who
 * reasonably expects them.
 *
 * They follow a summary-then-details shape: a plain-language "short version" a
 * racer will actually read, then numbered sections. The copy is the operator's
 * own policy about its own app, kept strictly to what SvcDue actually does — the
 * safety disclaimer especially, which is the one section a racer must not skip.
 *
 * These are the operator's own policy about its own app, not legal advice —
 * worth a lawyer's review before the public beta.
 *
 * These render outside the auth guard (App.tsx) so a signed-out visitor, or
 * someone on a public garage link, can reach them from the footer.
 */

import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { Eyebrow } from '../components/primitives';
import { Footer } from '../components/Footer';

const CONTACT = 'dragduroapp@gmail.com';

/** A paragraph, or a bullet list. Sections interleave the two in order. */
type Block = { p: string } | { ul: string[] };

interface Section {
  heading: string;
  blocks: Block[];
  /** The safety disclaimer earns a callout — the one section not to skim. */
  warn?: boolean;
}

interface LegalDocData {
  updated: string;
  summary: string[];
  sections: Section[];
}

/**
 * Renders a copy string with two affordances: `**bold**` spans, and the contact
 * email as a mailto link. Keeps the source copy readable while the important
 * phrases and the address still render as they should.
 */
function rich(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  text.split(/(\*\*[^*]+\*\*)/g).forEach((part, i) => {
    if (!part) return;
    const bold = part.startsWith('**') && part.endsWith('**');
    const content = bold ? part.slice(2, -2) : part;
    const inner = linkifyEmail(content, i);
    out.push(bold ? <strong key={`b${i}`}>{inner}</strong> : <Fragment key={`s${i}`}>{inner}</Fragment>);
  });
  return out;
}

function linkifyEmail(text: string, keyBase: number): ReactNode[] {
  const out: ReactNode[] = [];
  text.split(CONTACT).forEach((seg, j) => {
    if (j > 0) {
      out.push(
        <a key={`e${keyBase}-${j}`} href={`mailto:${CONTACT}`}>
          {CONTACT}
        </a>,
      );
    }
    if (seg) out.push(<Fragment key={`t${keyBase}-${j}`}>{seg}</Fragment>);
  });
  return out;
}

function LegalShell({ title }: { title: 'Terms of Service' | 'Privacy Policy' }) {
  return (
    <div className="svcdue">
      <main className="main legal">
        <Link to="/" className="link legal-back">
          <ArrowLeft size={13} /> Back to SvcDue
        </Link>
        <Eyebrow>Legal</Eyebrow>
        <h1 className="h1">{title}</h1>
        <div className="legal-body">
          <LegalDoc data={title === 'Terms of Service' ? TERMS : PRIVACY} />
        </div>
      </main>
      <Footer />
    </div>
  );
}

function LegalDoc({ data }: { data: LegalDocData }) {
  return (
    <>
      <p className="legal-updated">Last updated: {data.updated}</p>

      <div className="legal-summary">
        <div className="legal-summary-h">The short version</div>
        <ul>
          {data.summary.map((s, i) => (
            <li key={i}>{rich(s)}</li>
          ))}
        </ul>
      </div>

      {data.sections.map((sec, i) => (
        <section className={'legal-sec' + (sec.warn ? ' legal-sec-warn' : '')} key={i}>
          <h2 className="legal-sec-h">
            <span className="legal-sec-n">{i + 1}.</span>
            {sec.heading}
          </h2>
          {sec.blocks.map((block, j) =>
            'p' in block ? (
              <p key={j}>{rich(block.p)}</p>
            ) : (
              <ul key={j}>
                {block.ul.map((li, k) => (
                  <li key={k}>{rich(li)}</li>
                ))}
              </ul>
            ),
          )}
        </section>
      ))}
    </>
  );
}

const TERMS: LegalDocData = {
  updated: 'August 12, 2026',
  summary: [
    "SvcDue helps you track maintenance and safety-gear expirations for your race cars. It's a convenience tool — **it is not a substitute for your rulebook, your tech inspector, or your own judgment.**",
    "You're responsible for making sure your car and gear are actually safe and legal to race. Never rely on our reminders as your only check.",
    'Your data is yours. We only get the limited permission we need to run the app for you.',
    "Don't upload stuff you don't own, anything illegal or obscene, and don't attack the service.",
    "The service is provided as-is. If something breaks or a reminder doesn't arrive, our liability is limited.",
    'We can update these terms; if you keep using SvcDue after a change, that means you accept it.',
  ],
  sections: [
    {
      heading: "Who we are and what you're agreeing to.",
      blocks: [
        {
          p: 'SvcDue (svcdue.com) is operated by TheDigicar LLC ("we," "us"). By creating an account or using SvcDue, you agree to these Terms of Service. If you don\'t agree, don\'t use the service.',
        },
      ],
    },
    {
      heading: 'Safety disclaimer — read this one.',
      warn: true,
      blocks: [
        {
          p: 'SvcDue is an informational convenience tool. Its maintenance forecasts, expiration reminders, and "usual safety equipment" suggestions (which are based on general published NHRA E.T. brackets) are **not** a substitute for:',
        },
        {
          ul: [
            "the manufacturer's instructions for your parts and gear,",
            'the current rulebook of your sanctioning body (NHRA, IHRA, or whoever governs your class),',
            'a certified chassis inspector or track tech inspection, or',
            'your own inspection and judgment.',
          ],
        },
        {
          p: 'Rulebooks change, classes have exceptions, and our forecasts are only as good as the data you enter. Reminders may be missed, delayed, or wrong. You are solely responsible for the safety, legality, and race-readiness of your vehicles and equipment. Do not use SvcDue as your only check on anything that matters at the starting line.',
        },
      ],
    },
    {
      heading: 'Eligibility.',
      blocks: [
        {
          p: 'You must be old enough to form a binding contract where you live (the age of majority) to use SvcDue. SvcDue is not directed to anyone under 13.',
        },
      ],
    },
    {
      heading: 'Your account.',
      blocks: [
        {
          p: 'You sign in with your Google account; Google handles authentication and we never see or store a password. You\'re responsible for activity that happens under your account, so keep your Google account secure. Contact us at dragduroapp@gmail.com if you think your account has been misused.',
        },
      ],
    },
    {
      heading: 'Your content and our license to it.',
      blocks: [
        {
          p: 'Everything you enter — garages, cars, drivers, parts, gear, events, service logs, and photos — remains yours. You grant TheDigicar LLC only the limited, non-exclusive license needed to operate the service: to store, process, display, and transmit your content in order to run SvcDue for you and for anyone you choose to share it with (invited crew members, or the public if you turn on public sharing for a garage). This license ends when you delete the content or your account, except for brief periods needed to complete deletion from our systems.',
        },
      ],
    },
    {
      heading: 'Acceptable use.',
      blocks: [
        { p: 'You agree to:' },
        {
          ul: [
            'upload only content you have the right to use, including photos;',
            'not upload anything unlawful, obscene, or harassing;',
            "not probe, attack, overload, or otherwise abuse the service or try to access other users' data;",
            'not use SvcDue to spam or mislead people (for example, via garage sharing or crew invitations).',
          ],
        },
      ],
    },
    {
      heading: 'Sharing features.',
      blocks: [
        {
          p: "If you invite crew members to your garage, they can see that garage's data — invite people you trust. If you make a garage public, anyone with the link can view a read-only version of it; see the Privacy Policy for exactly what is and isn't shown. You're responsible for what you choose to share.",
        },
      ],
    },
    {
      heading: 'Suspension and termination.',
      blocks: [
        {
          p: 'We may suspend or terminate accounts that violate these terms, abuse the service, or create risk for other users. You can stop using SvcDue at any time and delete your account yourself from within the app.',
        },
      ],
    },
    {
      heading: 'Changes to the service.',
      blocks: [
        {
          p: "We may change, add, remove, or discontinue features of SvcDue, or the service itself. If we discontinue the service, we'll make reasonable efforts to give you notice in advance, so you have time to record or copy down anything you need to keep. To be straight with you about what that means today: SvcDue has no one-click data export, so recording your data means reading it out of the app. If we ever build an export, we'll say so here.",
        },
      ],
    },
    {
      heading: 'Disclaimer of warranties.',
      blocks: [
        {
          p: 'SvcDue is provided **"AS IS" and "AS AVAILABLE," without warranties of any kind**, express or implied, including fitness for a particular purpose, accuracy, or uninterrupted availability. We don\'t warrant that forecasts are correct, that reminder emails will be delivered, or that the service will be error-free.',
        },
      ],
    },
    {
      heading: 'Limitation of liability.',
      blocks: [
        {
          p: 'To the fullest extent permitted by law, TheDigicar LLC will not be liable for indirect, incidental, special, consequential, or punitive damages, or for loss of data, revenue, race results, or equipment, arising from your use of (or inability to use) SvcDue — including any missed, delayed, or incorrect reminder or forecast. Our total liability for any claim relating to the service will not exceed the greater of $50 or the amount you paid us for the service in the twelve months before the claim. Some jurisdictions don\'t allow certain limitations, so parts of this section may not apply to you.',
        },
      ],
    },
    {
      heading: 'Changes to these terms.',
      blocks: [
        {
          p: "We may update these terms. When we do, we'll post the new version here with an updated date. If you keep using SvcDue after a change takes effect, that means you accept the updated terms.",
        },
      ],
    },
    {
      heading: 'Governing law.',
      blocks: [
        {
          p: 'These terms are governed by the laws of the State of Florida, without regard to conflict-of-law rules.',
        },
      ],
    },
    {
      heading: 'Contact.',
      blocks: [{ p: 'Questions about these terms: dragduroapp@gmail.com.' }],
    },
  ],
};

const PRIVACY: LegalDocData = {
  updated: 'August 12, 2026',
  summary: [
    'We store your name, email, and Google account ID (from Google Sign-In), plus whatever you enter about your garage: cars, drivers, parts, gear, events, service logs, and photos.',
    'We use it for one thing: running the app and sending you reminder emails about your own gear. **No marketing email, ever.**',
    "We don't sell your data, show ads, or use third-party trackers. We count page views with our own anonymous counter — **your IP address is hashed and thrown away, never stored.**",
    'Your garage is private by default. Only you and crew you invite can see it — unless you deliberately make it public via an unguessable link.',
    'Google and Amazon Web Services run our sign-in, hosting, database, email, and photo storage, in the United States.',
    'You can delete your account and everything in it yourself, any time, right in the app.',
  ],
  sections: [
    {
      heading: 'What we collect and why.',
      blocks: [
        {
          p: 'SvcDue (svcdue.com) is operated by **TheDigicar LLC**, which is the data controller for everything described in this policy. You can reach us any time at dragduroapp@gmail.com.',
        },
        {
          p: "When you sign in with Google, we store your name, email address, and Google's stable account identifier so we can recognize your account. There's no SvcDue password — Google handles authentication. A sign-in token is kept in your browser to keep you signed in.",
        },
        {
          p: 'Everything else is data you enter to use the app: garages; cars (nickname, year/make/model, engine, class, odometer, optional photo); drivers (name, optional competition number); parts and safety gear (name, part number, spec such as SFI/Snell rating, expiration and recert dates, optional reorder links); race events (track, date, estimated and actual passes/miles); and service logs (date, optional cost, notes). Car photos are stored in cloud storage.',
        },
        {
          p: 'The plain-language legal basis for all of this: we process your account and garage data because it is **necessary to provide the service you asked for** — you cannot have an app that tracks your gear expirations without it holding your gear. The reminder emails are part of that same service. The anonymous page counters described below rest instead on our **legitimate interest** in knowing whether the app is being used at all, which is exactly why they are built so they cannot identify you. We don\'t use your data for anything else, and we never sell it.',
        },
      ],
    },
    {
      heading: 'Reminder emails.',
      blocks: [
        {
          p: 'SvcDue sends transactional emails only: a weekly digest of 60-day, 30-day and past-due expiration notices — and only when you actually have gear coming due or overdue. There is no marketing email, ever. Every reminder carries a one-click unsubscribe link, and you can turn either notice off in Account at any time; both stop the email without affecting what is tracked. To stop all email, delete your account (account deletion ends all email).',
        },
      ],
    },
    {
      heading: 'Analytics: how we count visits without identifying you.',
      blocks: [
        {
          p: "We use a small first-party counter, not a third-party analytics service. When you open a page it posts a **single allow-listed word** naming that page — `garage`, `account`, `privacy` — and nothing else. It never sends the address you are actually on. That distinction matters for one specific reason: a public garage link and a class-calculator URL carry a secret token in the address itself, so the counter is built to collapse every page to a fixed word rather than ever transmit the real path. The beacon also travels on its own route that forwards **no cookies and no sign-in token**, so it cannot be tied to your account even by accident.",
        },
        {
          p: "Like any request to any website, that beacon arrives with network metadata attached: your IP address, your browser's user-agent, and the content delivery network's own coarse guess at your city and country. **Your IP address is hashed with a salt and then discarded — we never store it in raw form**, and the location is city-level at best, which is simply what a CDN reports about any visitor. What we actually keep is an anonymous daily tally per page. There are no analytics cookies, no fingerprinting, and no third-party analytics scripts anywhere in SvcDue.",
        },
      ],
    },
    {
      heading: 'What we never do.',
      blocks: [
        {
          p: 'We do not sell your personal data. We do not show ads. We do not use third-party advertising or tracking, and we do not "share" personal information for cross-context behavioral advertising.',
        },
      ],
    },
    {
      heading: 'Who can see your data.',
      blocks: [
        {
          p: 'By default, only you. If you invite crew members to a garage by email, they can see that garage\'s data.',
        },
        {
          p: "Invitations work by email address, and deliberately only reach people who **already have a SvcDue account**. We look the address up; if no account matches, nothing happens and no account is created from it. We don't send mail to an address that isn't already a user, and an address that doesn't match is not retained.",
        },
        {
          p: "You can optionally make a garage public. This is off by default and works only through an unguessable link — there's no public directory or search. The public view is read-only and shows the fleet and the maintenance/expiration forecast, but it withholds car photos and driver personal details. Part numbers and reorder links appear publicly only if you separately opt in. Turn public sharing off at any time and the link stops working.",
        },
      ],
    },
    {
      heading: 'Service providers.',
      blocks: [
        { p: 'A few companies process data on our behalf, all in the United States:' },
        {
          ul: [
            '**Google** — sign-in (authentication).',
            '**Amazon Web Services** — hosting, database, and photo storage.',
            '**Resend** — delivery of the reminder emails we send you.',
            '**ImprovMX** — forwarding of any email you send to an @svcdue.com address.',
          ],
        },
        { p: 'They process your data only to provide these services to us.' },
      ],
    },
    {
      heading: 'Security.',
      blocks: [
        {
          p: 'Data is encrypted in transit, and our cloud provider encrypts data at rest. Access is scoped per user: the app serves you only your own garages and the ones you\'ve been invited to. No system is perfectly secure, and we won\'t pretend ours is — but we keep the design simple and the access rules strict.',
        },
      ],
    },
    {
      heading: 'Retention and deletion.',
      blocks: [
        {
          p: 'We keep your data until you delete it. The app has self-service account deletion that removes your account and every garage you own, along with all of its contents — cars, drivers, gear, events, logs, and photos. You can also delete individual items at any time.',
        },
        {
          p: 'There is **one deliberate exception**, and we would rather state it than let you discover it. If an administrator ever disables or deletes an account, we write an append-only record of that administrative action: which administrator did it, what they did, when, and a label identifying the account it was done to. That record **survives the deletion**, on purpose — a destructive action taken over someone\'s account that nobody can attribute afterwards is not an action worth being able to take. It exists for accountability and nothing else. It holds no garage content: no cars, gear, logs, or photos.',
        },
        {
          p: 'Anonymous page counts contain no personal information and may be kept indefinitely. Residual copies of deleted data may persist in encrypted backups for a limited period before being overwritten.',
        },
      ],
    },
    {
      heading: 'Where your data is processed.',
      blocks: [
        {
          p: 'SvcDue runs on infrastructure in the **United States**, and every provider listed above processes your data there. If you use SvcDue from the EEA, the UK, or anywhere else outside the US, your information is transferred to, stored in, and processed in the United States, where privacy law differs from your own country\'s. Where such a transfer requires a safeguard, we rely on the standard contractual clauses our providers offer in their data processing terms.',
        },
      ],
    },
    {
      heading: 'Children.',
      blocks: [
        {
          p: "SvcDue is not directed to anyone under 13, and we don't knowingly collect data from children under 13. If you believe a child has created an account, email us and we'll delete it.",
        },
      ],
    },
    {
      heading: 'Your choices and rights.',
      blocks: [
        {
          p: "You can view your data in the app, correct it by editing it, and delete it yourself — item by item or the whole account. If you'd rather have us handle a deletion, or you have any other question or request about your data, email dragduroapp@gmail.com and we'll take care of it.",
        },
        {
          p: '**If you are in the EEA, the UK, or Switzerland**, you have the right to access the personal data we hold about you, to have it corrected or erased, to receive a copy of it in a portable form, to object to or ask us to restrict how we process it, and to withdraw consent where we relied on it. You also have the right to complain to your local data protection supervisory authority.',
        },
        {
          p: "**If you are in California**, you have the right to know what we collect and why, to request access, correction, or deletion, and not to be treated differently for asking. We do not sell or share personal information for cross-context behavioral advertising, so there is no opt-out to exercise — there is nothing to opt out of.",
        },
        {
          p: "To exercise any of these, email dragduroapp@gmail.com. We verify requests against your account email and respond within the time applicable law allows. Because our page counters are genuinely anonymous, we cannot link them back to you, and so we cannot include them in an access or deletion request — there is nothing in them to find.",
        },
      ],
    },
    {
      heading: 'Changes to this policy.',
      blocks: [
        {
          p: 'If this policy changes, we\'ll post the updated version here with a new "Last updated" date. Material changes will be reflected in that date — check back if it\'s been a while.',
        },
      ],
    },
    {
      heading: 'Contact.',
      blocks: [{ p: 'Anything at all: dragduroapp@gmail.com.' }],
    },
  ],
};

export function Terms() {
  return <LegalShell title="Terms of Service" />;
}

export function Privacy() {
  return <LegalShell title="Privacy Policy" />;
}
